import { supabase } from '@/integrations/supabase/client';

export interface ScorekeeperGameRating {
  id: string;
  game_id: string;
  scorekeeper_id: string;
  on_time_score: 'A' | 'B' | 'C' | 'D' | 'F';
  accuracy_score: 'A' | 'B' | 'C' | 'D' | 'F';
  trust_score: 'A' | 'B' | 'C' | 'D' | 'F';
  overall_grade: 'A' | 'B' | 'C' | 'D' | 'F';
  corrections_needed: number;
  disputes_count: number;
  arrival_time?: string;
  game_completed: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ScorekeeperApproval {
  id: string;
  game_rating_id: string;
  approver_id: string;
  approver_type: 'player' | 'coach' | 'team_owner' | 'venue';
  approval_status: boolean;
  approval_comments?: string;
  approved_at?: string;
  created_at: string;
}

export interface ScorekeeperAchievement {
  id: string;
  scorekeeper_id: string;
  achievement_type: string;
  achievement_name: string;
  description?: string;
  icon_url?: string;
  points_awarded: number;
  earned_at: string;
  game_count_requirement?: number;
  grade_requirement?: string;
  is_special: boolean;
}

export interface ScorekeeperPerformanceMetrics {
  id: string;
  scorekeeper_id: string;
  total_games_scored: number;
  games_with_a_rating: number;
  games_with_b_rating: number;
  games_with_c_rating: number;
  games_with_d_rating: number;
  games_with_f_rating: number;
  perfect_games: number;
  tri_fecta_count: number;
  average_on_time_score: number;
  average_accuracy_score: number;
  average_trust_score: number;
  current_status: string;
  total_corrections: number;
  total_disputes: number;
  consecutive_perfect_games: number;
  big_player_eligible: boolean;
  created_at: string;
  updated_at: string;
}

export interface LiveGameSession {
  id: string;
  game_id: string;
  scorekeeper_id: string;
  session_status: string;
  home_team_score: number;
  away_team_score: number;
  current_period: number;
  time_remaining?: string;
  home_timeouts_left: number;
  away_timeouts_left: number;
  session_data?: any;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerGameStats {
  id: string;
  game_id: string;
  player_id: string;
  player_type: string;
  team_side: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  fouls: number;
  turnovers: number;
  field_goals_made: number;
  field_goals_attempted: number;
  free_throws_made: number;
  free_throws_attempted: number;
  three_pointers_made: number;
  three_pointers_attempted: number;
  minutes_played: number;
  plus_minus: number;
  sport_specific_stats?: any;
  recorded_by: string;
  approved_by_player: boolean | null;
  approved_by_coach: boolean | null;
  approved_by_venue: boolean | null;
  created_at: string;
  updated_at: string;
}

export const scorekeeperService = {
  // Game Rating Functions
  async createGameRating(rating: Omit<ScorekeeperGameRating, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('scorekeeper_game_ratings')
      .insert(rating)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getGameRatingsByScorekeeper(scorekeeperId: string) {
    const { data, error } = await supabase
      .from('scorekeeper_game_ratings')
      .select('*')
      .eq('scorekeeper_id', scorekeeperId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async updateGameRating(ratingId: string, updates: Partial<ScorekeeperGameRating>) {
    const { data, error } = await supabase
      .from('scorekeeper_game_ratings')
      .update(updates)
      .eq('id', ratingId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Approval Functions
  async submitApproval(approval: Omit<ScorekeeperApproval, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('scorekeeper_approvals')
      .insert(approval)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getApprovalsForRating(ratingId: string) {
    const { data, error } = await supabase
      .from('scorekeeper_approvals')
      .select('*')
      .eq('game_rating_id', ratingId);
    
    if (error) throw error;
    return data;
  },

  // Performance Metrics Functions
  async getPerformanceMetrics(scorekeeperId: string) {
    const { data, error } = await supabase
      .from('scorekeeper_performance_metrics')
      .select('*')
      .eq('scorekeeper_id', scorekeeperId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getAchievements(scorekeeperId: string) {
    const { data, error } = await supabase
      .from('scorekeeper_achievements')
      .select('*')
      .eq('scorekeeper_id', scorekeeperId)
      .order('earned_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Live Game Session Functions
  async createLiveSession(session: Omit<LiveGameSession, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('live_game_sessions')
      .insert(session)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateLiveSession(sessionId: string, updates: Partial<LiveGameSession>) {
    const { data, error } = await supabase
      .from('live_game_sessions')
      .update(updates)
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getLiveSession(gameId: string, scorekeeperId: string) {
    const { data, error } = await supabase
      .from('live_game_sessions')
      .select('*')
      .eq('game_id', gameId)
      .eq('scorekeeper_id', scorekeeperId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async getActiveSessions(scorekeeperId: string) {
    const { data, error } = await supabase
      .from('live_game_sessions')
      .select('*')
      .eq('scorekeeper_id', scorekeeperId)
      .in('session_status', ['pre_game', 'in_progress', 'paused'])
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Player Game Stats Functions
  async createPlayerGameStats(stats: Omit<PlayerGameStats, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('player_game_stats')
      .insert(stats)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updatePlayerGameStats(statsId: string, updates: Partial<PlayerGameStats>) {
    const { data, error } = await supabase
      .from('player_game_stats')
      .update(updates)
      .eq('id', statsId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getPlayerGameStats(gameId: string) {
    const { data, error } = await supabase
      .from('player_game_stats')
      .select('*')
      .eq('game_id', gameId)
      .order('team_side', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  async bulkUpdatePlayerStats(statsUpdates: Array<{ id: string; updates: Partial<PlayerGameStats> }>) {
    const promises = statsUpdates.map(({ id, updates }) =>
      this.updatePlayerGameStats(id, updates)
    );
    
    return Promise.all(promises);
  },

  // Status Calculation Functions
  getStatusInfo(status: string) {
    const statusMap = {
      'not_qualified': { 
        label: 'Not Qualified', 
        color: 'text-destructive', 
        bgColor: 'bg-destructive/10',
        description: 'F grade scores - blocked from assignments'
      },
      'needs_improvement': { 
        label: 'Needs Improvement', 
        color: 'text-orange-600', 
        bgColor: 'bg-orange-100',
        description: 'Majority D grades - limited assignments'
      },
      'bronze': { 
        label: 'Bronze', 
        color: 'text-yellow-600', 
        bgColor: 'bg-yellow-100',
        description: 'Standard performance level'
      },
      'qualified_scorekeeper': { 
        label: 'Qualified ScoreKeeper', 
        color: 'text-blue-600', 
        bgColor: 'bg-blue-100',
        description: '10+ games at A/B rating'
      },
      'on_the_rise': { 
        label: 'On the Rise', 
        color: 'text-green-600', 
        bgColor: 'bg-green-100',
        description: '25+ games at A/B rating - priority assignments'
      },
      'no_cap': { 
        label: 'No Cap', 
        color: 'text-purple-600', 
        bgColor: 'bg-purple-100',
        description: '50+ games at A/B rating - premium assignments'
      },
      'big_player': { 
        label: 'Big Player', 
        color: 'text-red-600', 
        bgColor: 'bg-red-100',
        description: '60+ perfect games - exclusive access'
      }
    };
    
    return statusMap[status as keyof typeof statusMap] || statusMap.bronze;
  },

  getGradeColor(grade: string) {
    const gradeColors = {
      'A': 'text-green-600 bg-green-100',
      'B': 'text-blue-600 bg-blue-100',
      'C': 'text-yellow-600 bg-yellow-100',
      'D': 'text-orange-600 bg-orange-100',
      'F': 'text-red-600 bg-red-100'
    };
    
    return gradeColors[grade as keyof typeof gradeColors] || 'text-gray-600 bg-gray-100';
  }
};